import type { McpAccessLevel } from '@lib/enums/mcp-access-level.enum'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import * as React from 'react'
import { FlaskConical, Inbox, Plus } from 'lucide-react'
import { EnvelopeStatusBadge, SignerStatusBadge } from '@/components/envelopes/StatusBadge'
import { PageHeader } from '@/components/PageHeader'
import { RecordFacts, RecordHeadline, RecordSection, RecordSheet } from '@/components/RecordSheet'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { Tooltip } from '@/components/ui/tooltip'

const SWATCHES: { name: string; className: string }[] = [
  { name: 'canvas', className: 'bg-canvas' },
  { name: 'card (surface)', className: 'bg-card' },
  { name: 'subtle', className: 'bg-subtle' },
  { name: 'accent (hover)', className: 'bg-accent' },
  { name: 'selected', className: 'bg-selected' },
  { name: 'border', className: 'bg-border' },
  { name: 'border-strong', className: 'bg-border-strong' },
  { name: 'foreground', className: 'bg-foreground' },
  { name: 'muted-foreground', className: 'bg-muted-foreground' },
  { name: 'primary', className: 'bg-primary' },
  { name: 'primary-subtle', className: 'bg-primary-subtle' },
  { name: 'destructive', className: 'bg-destructive' },
  { name: 'success-subtle', className: 'bg-success-subtle' },
  { name: 'warning-subtle', className: 'bg-warning-subtle' },
  { name: 'danger-subtle', className: 'bg-danger-subtle' },
  { name: 'info-subtle', className: 'bg-info-subtle' },
  { name: 'attention (signing)', className: 'bg-attention' },
  { name: 'attention-solid', className: 'bg-attention-solid' },
]

/** Both segmented-control looks: a plain choice, and choices with real-world weight in the warning tone. */
function SegmentedDemo() {
  const [mode, setMode] = React.useState<TenantMode>('test')
  const [access, setAccess] = React.useState<McpAccessLevel>('live_read')
  return (
    <Card>
      <CardContent className="grid gap-4 pt-card sm:grid-cols-2">
        <div className="space-y-1">
          <p className="text-caption text-muted-foreground">Mode (icon label, full width)</p>
          <SegmentedControl
            ariaLabel="Mode"
            value={mode}
            onChange={setMode}
            fullWidth
            options={[
              { value: 'live', label: 'Live' },
              {
                value: 'test',
                label: (
                  <>
                    <FlaskConical />
                    Test
                  </>
                ),
                ariaLabel: 'Test',
                tone: 'warning',
              },
            ]}
          />
        </div>
        <div className="space-y-1">
          <p className="text-caption text-muted-foreground">Access level (one option disabled)</p>
          <SegmentedControl
            ariaLabel="Access level"
            value={access}
            onChange={setAccess}
            options={[
              { value: 'test_only', label: 'Test only' },
              { value: 'live_read', label: 'Live read-only', tone: 'warning' },
              { value: 'live', label: 'Live', tone: 'warning', disabled: true },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-title font-semibold">{title}</h2>
        {description ? <p className="text-body text-muted-foreground">{description}</p> : null}
      </div>
      {children}
    </section>
  )
}

const SAMPLE_REQUESTS = [
  { id: 'sample-1', method: 'POST', path: '/v1/envelopes', status: 201, ms: 184, body: '{\n  "title": "Loan packet",\n  "send": true\n}' },
  { id: 'sample-2', method: 'GET', path: '/v1/envelopes/env_8f2c', status: 200, ms: 22, body: null },
  { id: 'sample-3', method: 'POST', path: '/v1/envelopes', status: 422, ms: 9, body: '{\n  "title": ""\n}' },
] as const

/** A small list that opens its rows in the record tray, as the request, webhook, and mailbox pages do. */
function RecordTrayDemo() {
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Request</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {SAMPLE_REQUESTS.map((row) => (
            <TableRow
              key={row.id}
              data-record-id={row.id}
              className={row.id === selectedId ? 'cursor-pointer bg-selected' : 'cursor-pointer'}
              onClick={() => setSelectedId(row.id)}
            >
              <TableCell>
                <button type="button" className="font-mono text-caption text-link">
                  {row.method} {row.path}
                </button>
              </TableCell>
              <TableCell>
                <Badge tone={row.status >= 400 ? 'warning' : 'success'}>{row.status}</Badge>
              </TableCell>
              <TableCell className="text-right text-muted-foreground">{row.ms} ms</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <RecordSheet title="API request" ids={SAMPLE_REQUESTS.map((row) => row.id)} selectedId={selectedId} onSelect={setSelectedId}>
        {(id) => {
          const row = SAMPLE_REQUESTS.find((candidate) => candidate.id === id)
          if (!row) return null
          return (
            <>
              <RecordHeadline
                facts={
                  <RecordFacts>
                    <dt className="text-muted-foreground">Request id</dt>
                    <dd className="font-mono">req_{row.id}</dd>
                    <dt className="text-muted-foreground">Time</dt>
                    <dd>{row.ms} ms</dd>
                  </RecordFacts>
                }
              >
                <h3 className="font-mono text-body-lg font-semibold">
                  {row.method} {row.path}
                </h3>
                <Badge tone={row.status >= 400 ? 'warning' : 'success'}>{row.status}</Badge>
                <Button variant="secondary" size="sm" className="ml-auto">
                  Secondary action
                </Button>
              </RecordHeadline>
              <RecordSection title="Request body">
                <pre className="font-mono text-caption whitespace-pre-wrap">{row.body ?? '(none)'}</pre>
              </RecordSection>
            </>
          )
        }}
      </RecordSheet>
    </>
  )
}

/**
 * The design system rendered with the real components, in whichever theme is
 * active. Staff-only. Use it to review token or primitive changes in light and
 * dark before shipping (design-system/README.md).
 */
export function OpsDesignPage() {
  return (
    <>
      <PageHeader
        title="Design system"
        description="Tokens and primitives as the app renders them. Switch themes to review both."
        actions={<ThemeToggle />}
      />
      <div className="space-y-10">
        <Section title="Color roles" description="Semantic roles only; components never use the primitive ramps.">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {SWATCHES.map((swatch) => (
              <div key={swatch.name} className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
                <div className={`h-12 border-b border-border ${swatch.className}`} />
                <p className="px-2 py-1.5 text-caption text-muted-foreground">{swatch.name}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Type" description="Weight-first: sizes stay close; headings differ by weight and color.">
          <Card>
            <CardContent className="space-y-2 pt-card">
              <p className="text-heading font-semibold">Heading 20 / semibold</p>
              <p className="text-title font-semibold">Title 16 / semibold</p>
              <p className="text-body-lg font-medium">Body large 14 / medium</p>
              <p className="text-body">Body 13 / regular: the default for dashboard text and tables.</p>
              <p className="text-caption text-muted-foreground">Caption 12 / secondary: meta, hints, table headers.</p>
              <p className="font-mono text-caption">mono: env_9f2c1a… · psk_live_… · 12,480.00</p>
            </CardContent>
          </Card>
        </Section>

        <Section title="Buttons" description="One primary per view. Destructive is quiet in place; solid red only confirms.">
          <div className="flex flex-wrap items-center gap-2">
            <Button>
              <Plus />
              Primary
            </Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive-quiet">Remove</Button>
            <Button variant="destructive">Confirm delete</Button>
            <Button variant="link">Link action</Button>
            <Button disabled>Disabled</Button>
            <Button size="sm" variant="secondary">
              Small
            </Button>
          </div>
        </Section>

        <Section title="Fields" description="Every control sits in a Field: label above, hint or error below.">
          <Card>
            <CardContent className="grid gap-4 pt-card sm:grid-cols-2">
              <Field label="Email" htmlFor="ds-email" hint="We'll send the invitation here.">
                <Input id="ds-email" placeholder="teammate@example.com" />
              </Field>
              <Field label="Role" htmlFor="ds-role">
                <Select id="ds-role" defaultValue="developer">
                  <option value="owner">Owner</option>
                  <option value="developer">Developer</option>
                  <option value="viewer">Viewer</option>
                </Select>
              </Field>
              <Field label="Webhook URL" htmlFor="ds-url" error="Must be an https:// URL.">
                <Input id="ds-url" defaultValue="http://example.com/hook" aria-invalid />
              </Field>
              <Field label="Disabled" htmlFor="ds-disabled">
                <Input id="ds-disabled" disabled defaultValue="Read only" />
              </Field>
              <Field label="Notes" htmlFor="ds-notes" className="sm:col-span-2">
                <Textarea id="ds-notes" placeholder="Optional" rows={3} />
              </Field>
            </CardContent>
          </Card>
        </Section>

        <Section
          title="Segmented control"
          description="Two to four exclusive choices side by side. Arrow keys move the choice; warning marks weighty ones."
        >
          <SegmentedDemo />
        </Section>

        <Section title="Badges" description="Muted status pairs.">
          <div className="flex flex-wrap gap-2">
            <Badge>Neutral</Badge>
            <Badge tone="accent">Owner</Badge>
            <Badge tone="success">Success</Badge>
            <Badge tone="warning">Warning</Badge>
            <Badge tone="danger">Danger</Badge>
            <Badge tone="info">Info</Badge>
            <EnvelopeStatusBadge status="pending" />
            <EnvelopeStatusBadge status="completed" />
            <SignerStatusBadge status="declined" />
          </div>
        </Section>

        <Section title="Table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Envelope</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Signers</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium text-link">ACH authorization</TableCell>
                <TableCell>
                  <EnvelopeStatusBadge status="pending" />
                </TableCell>
                <TableCell className="text-right">2</TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">Oct 2, 2026, 11:24 AM</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-link">Closing disclosure</TableCell>
                <TableCell>
                  <EnvelopeStatusBadge status="completed" />
                </TableCell>
                <TableCell className="text-right">1</TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">Oct 1, 2026, 4:02 PM</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>

        <Section
          title="List and detail"
          description="Full-width table; a row opens the record tray from the right. Step with ↑/↓ or K/J; Escape closes."
        >
          <RecordTrayDemo />
        </Section>

        <Section title="Overlays and feedback">
          <div className="flex flex-wrap items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="secondary">Open dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Remove Victor Viewer?</DialogTitle>
                  <DialogDescription>They lose access right away. You can invite them again later.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="secondary">Cancel</Button>
                  <Button variant="destructive">Remove</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Tooltip content="Tooltips replace the native title attribute">
              <Button variant="secondary">Hover for tooltip</Button>
            </Tooltip>
            <Button variant="secondary" onClick={() => toast.success('Invitation sent to dev@example.com')}>
              Success toast
            </Button>
            <Button variant="secondary" onClick={() => toast.error('Could not reach the webhook URL')}>
              Error toast
            </Button>
          </div>
        </Section>

        <Section title="Page states" description="Every list and detail surface handles loading, empty, and error.">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Loading</CardTitle>
              </CardHeader>
              <LoadingState />
            </Card>
            <EmptyState
              icon={Inbox}
              title="No envelopes yet"
              description="Calling apps haven't sent anything."
              action={<Button size="sm">Read the API docs</Button>}
            />
            <div>
              <ErrorState message="Could not load the team." />
            </div>
          </div>
        </Section>

        <Section
          title="Signing attention"
          description="Required fields on the signing page: orange, independent of the tenant's brand. Optional fields are dashed and gray, so orange always means Finish waits on it. Signer pages are light, or dark in an embed whose host asks; documents stay white."
        >
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-card shadow-card">
            <span className="sign-field-empty inline-flex h-9 w-40 items-center rounded-sm border px-2 text-caption">
              Sign here
            </span>
            <span className="sign-field-optional inline-flex h-9 w-40 items-center rounded-sm border px-2 text-caption text-muted-foreground">
              Optional
            </span>
            <span className="sign-next-bar inline-flex h-control items-center rounded-md px-3 font-medium">
              Next field
            </span>
          </div>
        </Section>

        <Card>
          <CardHeader>
            <CardTitle>Source</CardTitle>
            <CardDescription>
              Values in design-system/tokens.css; rules in the project-design-system skill; component conventions in
              design-system/COMPONENTS.md.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </>
  )
}
